<script setup>
import {computed, onMounted, ref, watch} from 'vue';
import { useNotificationStore } from '@/stores/NotificationStore.js'
import counts from "./_counts.vue";
    const notificationStore = useNotificationStore()

    const emit = defineEmits(['tagsSelected', 'close', 'doReloadLeads']);

    const props = defineProps({
        preSelectedTags: {
            type: Array,
            required: false,
            default: () => []
        },
        allowCreation: {
            type: Boolean,
            required: false,
            default: true
        },
        placeholder: {
            type: String,
            required: false,
            default: _mft('crm:tag.searchTags')
        },
        selectMultiple: {
            type: Boolean,
            required: false,
            default: true
        },
        displayCounts: {
            type: Array,
            required: false,
            default: () => [], // total, email, phone
        },
        displayAs: {
            type: String,
            required: false,
            default: 'pill' // pill, list
        }
    });

    const availableTags = ref([]);
    const search = ref('');
    const showAll = ref(false);
    const selectedTags = ref([]);
    const showSuggestions = ref(false);

    const syncSelectedTagsFromProps = () => {
        if (availableTags.value.length === 0) {
            return;
        }

        const nextSelectedTags = props.preSelectedTags
            .map((tagIdentifier) => availableTags.value.find((tag) => (
                tag.id === tagIdentifier || tag.uuid === tagIdentifier
            )))
            .filter(Boolean);

        const currentSelectedTagIds = selectedTags.value.map((tag) => tag.id);
        const nextSelectedTagIds = nextSelectedTags.map((tag) => tag.id);

        if (JSON.stringify(currentSelectedTagIds) !== JSON.stringify(nextSelectedTagIds)) {
            selectedTags.value = nextSelectedTags;
        }
    };

    const loadTags = () => {
        axios.get(route('crm.leadtags.index')).then((response) => {
            availableTags.value = response.data.tags;
            syncSelectedTagsFromProps();
        });
    }

    const createTag = () => {
        axios.post(route('crm.leadtags.store'), {
            name: search.value
        }).then((response) => {
            loadTags();
        });
    }

    const selectTag = (tagId) => {
        if (props.selectMultiple) {
            if (selectedTags.value.includes(tagId)) {
                selectedTags.value = selectedTags.value.filter((id) => id !== tagId);
            } else {
                selectedTags.value.push(tagId);
            }
        } else {
            selectedTags.value = [tagId];
            search.value = ''
            showSuggestions.value = false;
        }
    }

    const onSelectionDone = () => {
        showSuggestions.value = false
    }

    const clearSelectedTags = () => {
        search.value = ''
        selectedTags.value = []
        showSuggestions.value = false
    }

    const filteredTags = computed(() => {
        return availableTags.value.filter((tag) => {
            return tag.name.toLowerCase().includes(search.value.toLowerCase());
        });
    });

    const limitedFilteredTags = computed(() => {
        if (showAll.value === true) {
            return filteredTags.value;
        } else {
            return filteredTags.value.slice(0, 5);
        }
    });

    const moreTagsAvailable = computed(() => {
        return filteredTags.value.length - 5;
    });

    const tagListClass = computed(() => {
        if (props.displayAs === 'list') {
            return 'flex flex-col max-h-60 overflow-y-scroll';
        }
        return 'mb-4 flex flex-wrap'
    })

    const selectedTagListClass = computed(() => {
        if (props.displayAs === 'list') {
            return 'flex flex-col';
        }
        return 'mb-4 flex flex-wrap'
    })

    const tagListItemClass = computed(() => {
        if (props.displayAs === 'list') {
            return 'flex justify-between items-start w-full';
        }
        return 'flex items-center'
    })

    watch(search, async (newVal, oldVal) => {
       if (newVal.length === 0) {
           // showSuggestions.value = false
       }
    });

    watch(
        [() => props.preSelectedTags, availableTags],
        () => {
            syncSelectedTagsFromProps();
        },
        {deep: true}
    );

    watch(selectedTags,  (newVal, oldVal) => {
        emit('tagsSelected', selectedTags.value);
    }, {deep: true});

    onMounted(() => {
        loadTags();

        // On esc pressed, set showSuggestions = false
        window.addEventListener('keydown', (e) => {
            if (e.key === 'Escape') {
                showSuggestions.value = false;
            }
        });
    });

    defineExpose({clearSelectedTags})

</script>
<template>
    <div>
        <div
            v-if="showSuggestions"
            @click="showSuggestions = false"
            class="fixed inset-0 z-40"
        ></div>

        <div
            class="relative"
            :class="showSuggestions ? 'z-50' : ''"
        >
            <div class="relative mb-2">
                <input-text
                    class="!mb-0 z-10"
                    v-model:content="search"
                    :maxlength="24"
                    :placeholder="placeholder"
                    @focus="showSuggestions = true"
                    @click="showSuggestions = true"
                />
                <div class="absolute top-3 right-1 flex items-center space-x-1 -translate-y-px">
                    <button
                        v-if="search && allowCreation"
                        class="rounded-md bg-black px-2 py-1 text-white text-xs"
                        @click="createTag"
                    >{{ _mft('crm:tag.createNew') }}</button>

                    <button
                        v-if="search.length === 0 && showSuggestions"
                        class="rounded-md bg-wlPrimary px-2 py-1 text-white text-xs"
                        @click="onSelectionDone"
                    >{{ _mft('shared:done') }}</button>

                    <button
                        v-if="search.length > 0"
                        class="rounded-md bg-gray-400 px-2 py-1 text-white text-xs"
                        @click="search = ''"
                    >{{ _mft('shared:action.clear') }}</button>
                </div>

                <ul
                    v-if="showSuggestions || search.length > 0"
                    class="p-2 bg-white rounded-md shadow-2xl absolute top-full left-0 right-0 z-50"
                    :class="tagListClass"
                >
                    <li
                        v-for="tag in limitedFilteredTags"
                        :key="tag.id"
                        @click="selectTag(tag)"
                        class="cursor-pointer text-black px-1.5 py-0.5 border border-gray-300 bg-gray-200 hover:bg-gray-300 rounded-lg"
                        :class="selectedTags.includes(tag) ? 'hidden' : 'my-0.5 mr-1'"
                        :style="tag.color ? {backgroundColor: tag.color, color: tag.textcolor} : {}"
                    >
                        <div class="flex items-center space-x-1 text-xs">
                            <component
                                :is="tag.description && displayAs === 'pill' ? 'container-with-tooltip' : 'span'"
                                :class="displayAs === 'list' ? tagListItemClass : ''"
                            >
                                <div>
                                    <div>{{ tag.name }}</div>
                                    <div
                                        v-if="displayAs === 'list'"
                                        class="text-xxs opacity-70"
                                    >
                                        {{ tag.description }}
                                    </div>
                                </div>
                                <counts
                                    v-if="displayCounts.length > 0"
                                    :tag="tag"
                                    :display-counts="displayCounts"
                                />
                                <template
                                    v-if="tag.description"
                                    #tooltip
                                >
                                    <div class="block w-24 text-xxs text-black">
                                        {{ tag.description }}
                                    </div>
                                </template>
                            </component>
                        </div>
                    </li>
                    <li
                        v-if="limitedFilteredTags.length === 0"
                        class="mr-2 mt-2 text-gray-400"
                    >{{ _mft('crm:tag.noTagsFound', {count: moreTagsAvailable }) }}</li>
                    <li
                        v-if="moreTagsAvailable > 0 && !showAll"
                        class="mr-2 cursor-pointer mt-2 bg-white text-xs text-gray-500"
                        @click="showAll = true"
                    >
                        <span class="mf-link">{{ _mft('crm:tag.andAnotherNMore', {count: moreTagsAvailable }) }}</span>...</li>
                    <li
                        v-if="search.length > 0"
                        class="flex justify-end"
                    >
                        <button
                            v-if="search.length === 0 && showSuggestions"
                            class="rounded-md bg-wlPrimary px-2 py-1 text-white"
                            @click="showSuggestions = false"
                        >{{ _mft('shared:done') }}</button>
                    </li>
                </ul>
            </div>
            <ul
                :class="selectedTagListClass"
            >
                <li
                    v-for="tag in selectedTags"
                    :key="tag.id"
                    class="mr-1 mb-1.5"
                >
                    <pill
                        class="border border-gray-300 bg-gray-200 text-black flex cursor-default"
                        size="sm"
                        :style="tag.color ? {backgroundColor: tag.color, color: tag.textcolor} : {}"
                    >
                        <div
                            :class="tagListItemClass"
                        >
                            <component
                                :is="tag.description ? 'container-with-tooltip' : 'span'"
                            >
                                <div>{{ tag.name }}</div>
                                <counts
                                    v-if="displayCounts.length > 0"
                                    :tag="tag"
                                    :display-counts="displayCounts"
                                    class="opacity-70"
                                />
                                <template
                                    v-if="tag.description"
                                    #tooltip
                                >
                                    <div class="block w-24 text-xxs">
                                        {{ tag.description }}
                                    </div>
                                </template>
                            </component>

                            <span
                                class="bg-white hover:bg-gray-100 text-black w-4 h-4 rounded-full flex items-center justify-center cursor-pointer translate-x-1"
                                @click="selectTag(tag)"
                            >
                                <i class="fa-solid fa-xmark"></i>
                            </span>
                        </div>
                    </pill>
                </li>
            </ul>
        </div>
    </div>
</template>
